/**
 * React Query Key Factory
 *
 * Centralized query key definitions to ensure:
 * - Type safety
 * - Consistent key structure
 * - Route-keyed caching (prevents cross-route stale renders)
 * - Easy invalidation patterns
 *
 * Pattern: [domain, ...identifiers]
 */

import type { MediaType } from '../types/movie';

/**
 * API-relevant filter params for discover query keys.
 * Only includes filters that affect the API call (not client-side filters).
 */
export interface DiscoverParams {
  genres: number[];
  yearRange: [number, number];
  runtimeRange: [number, number];
  languages: string[];
  actorSearch: string[];
  directorSearch: string[];
  sortBy: string;
  popularOnly: boolean;
  releasedOnly: boolean;
  ratingRange: [number, number];
  rtRatingRange: [number, number];
  tmdbRatingRange: [number, number];
  ourRatingRange: [number, number];
}

export const queryKeys = {
  // For You page (Phase 1 - Foundation + Phase 2.0 AI Cache)
  forYou: {
    // User's top-rated movies for shelf anchors
    topRatings: (userId: string) => ['forYou', 'topRatings', userId] as const,
    // Shelf data (cached per user + taste profile version)
    // tasteProfileVersion ensures recompute when taste profile changes
    shelves: (userId: string, tasteProfileVersion: string) =>
      ['forYou', 'shelves', userId, tasteProfileVersion] as const,
    // User's taste profile for cold start
    tasteProfile: (userId: string) => ['forYou', 'tasteProfile', userId] as const,
    // Phase 2.0: GET /api/for-you (backend cache with AI hero)
    // Includes entitlement tier + mediaType to isolate cache per tier and scope
    api: (userId: string, tier: 'free' | 'social' | 'pro', mediaType: 'movie' | 'tv') => ['forYou', 'api', userId, tier, mediaType] as const,
    // Base key for invalidating all tiers + media types for a user (use for tier toggles or full refresh)
    apiBase: (userId: string) => ['forYou', 'api', userId] as const,
  },

  // Discover (Phase 5G - infinite scroll grid)
  discover: {
    all: () => ['discover'] as const,
    // List with filters - includes all API-relevant params
    list: (mediaType: MediaType, searchQuery: string | null, params: DiscoverParams) =>
      ['discover', mediaType, searchQuery, params] as const,
    // Base key for invalidating all discover queries
    base: (mediaType: MediaType) => ['discover', mediaType] as const,
  },

  // Media detail pages
  movie: {
    detail: (movieId: number) => ['movie', movieId] as const,
  },

  tv: {
    detail: (tvId: number) => ['tv', tvId] as const,
  },

  person: {
    detail: (personId: number) => ['person', personId] as const,
  },

  // User profiles
  profile: {
    byUsername: (usernameLower: string) => ['profile', usernameLower] as const,
  },

  // User ratings
  rating: {
    one: (userId: string, mediaType: 'movie' | 'tv', mediaId: number) =>
      ['rating', userId, mediaType, mediaId] as const,
    list: (userId: string) => ['ratings', userId] as const,
  },

  // Watchlist follows (Phase 3.1)
  watchlist: {
    followed: (userId: string) => ['watchlists', 'followed', userId] as const,
    followStatus: (userId: string, watchlistId: string) =>
      ['watchlist', watchlistId, 'followStatus', userId] as const,
  },

  // Lists (Phase 4A - React Query migration)
  lists: {
    // User's owned + collab lists (sidebar source of truth)
    mine: (userId: string) => ['lists', 'mine', userId] as const,
    // Base key for invalidating all user lists
    mineBase: () => ['lists', 'mine'] as const,
    // Single list metadata
    detail: (listId: string) => ['lists', listId, 'detail'] as const,
    // List items with cursor pagination (canonical: position asc)
    items: (listId: string, params?: { page?: number; limit?: number }) =>
      ['lists', listId, 'items', { page: params?.page ?? 1, limit: params?.limit ?? 100 }] as const,
    // Base key for invalidating all items for a list
    itemsBase: (listId: string) => ['lists', listId, 'items'] as const,
    // Batch membership check (which lists contain a media item)
    membership: (mediaId: number, mediaType: 'movie' | 'tv', listIds: string[]) =>
      ['lists', 'membership', mediaId, mediaType, listIds.sort().join(',')] as const,
    // Public lists for other user's profiles (defaults + public visibility)
    publicLists: (userId: string) => ['lists', userId, 'public'] as const,
    // Soft-deleted watchlists (Spotify-style recovery)
    deleted: (userId: string, params?: { page?: number; limit?: number }) =>
      ['lists', 'deleted', userId, { page: params?.page ?? 1, limit: params?.limit ?? 20 }] as const,
    // Base key for invalidating all deleted list queries
    deletedBase: (userId: string) => ['lists', 'deleted', userId] as const,
    // Collaborators for a watchlist
    collaborators: (listId: string) => ['lists', listId, 'collaborators'] as const,
  },

  // Notifications (Phase 3.1)
  notifications: {
    unreadCount: (userId: string) => ['notifications', userId, 'unreadCount'] as const,
    list: (userId: string, params: { limit: number; cursor?: string | null; type?: string | null }) =>
      ['notifications', userId, 'list', { limit: params.limit, cursor: params.cursor ?? null, type: params.type ?? null }] as const,
    // Base key for invalidating all notification lists for a user
    listBase: (userId: string) => ['notifications', userId, 'list'] as const,
  },

  // Inbox - Instagram-style threads grouped by sender (Phase 4)
  inbox: {
    // Threads list (people list) - infinite scroll
    // Includes limit in key for bounded persistence (cursor=null, limit<=20)
    threads: (userId: string, params?: { cursor?: string | null; limit?: number }) =>
      ['inbox', userId, 'threads', { cursor: params?.cursor ?? null, limit: params?.limit ?? 20 }] as const,
    // Base key for invalidating all thread lists for a user
    threadsBase: (userId: string) => ['inbox', userId, 'threads'] as const,
    // Threads preview - simple query for dropdown (non-infinite)
    threadsPreview: (userId: string, params?: { limit?: number }) =>
      ['inbox', userId, 'threadsPreview', { limit: params?.limit ?? 5 }] as const,
    // Thread items (specific sender)
    thread: (userId: string, fromUserId: string, params?: { cursor?: string | null }) =>
      ['inbox', userId, 'thread', fromUserId, { cursor: params?.cursor ?? null }] as const,
    // Base key for invalidating a specific thread
    threadBase: (userId: string, fromUserId: string) => ['inbox', userId, 'thread', fromUserId] as const,
    // Unread count for inbox types only (separate from notifications bell)
    unreadCount: (userId: string) => ['inbox', userId, 'unreadCount'] as const,
  },

  // Social feed (Phase 5F)
  social: {
    // Feed with filter + pagination params
    // Bounded persistence: only cursor=null, limit<=20 is persisted
    feed: (userId: string, params: { filter: string; cursor?: string | null; limit?: number }) =>
      ['social', userId, 'feed', { filter: params.filter, cursor: params.cursor ?? null, limit: params.limit ?? 20 }] as const,
    // Base key for invalidating all social feed queries for a user (used by realtime)
    feedBase: (userId: string) => ['social', userId, 'feed'] as const,
  },

  // User-specific data (recent searches)
  user: {
    recentSearches: (userId: string) => ['user', userId, 'recentSearches'] as const,
    searchHistory: (userId: string) => ['user', userId, 'searchHistory'] as const,
  },

  // Unified search (/search/all) - viewer-scoped for personalization
  search: {
    // viewerBucket = auth.user?.id ?? 'anon' (cache varies by viewer for personalization)
    all: (viewerBucket: string, query: string) =>
      ['search', 'all', viewerBucket, query] as const,
  },

  // /feel page - emotion-based discovery
  feel: {
    // Results for selected emotions (emotions sorted for stable cache key)
    // Uses ai_emotions_evoked filter in Meilisearch
    results: (emotions: string[], sortBy: string, limit: number) =>
      ['feel', 'results', [...emotions].sort().join(','), sortBy, limit] as const,
  },

  // Blocked users
  blocked: {
    list: (userId: string) => ['blocked', 'list', userId] as const,
    listBase: () => ['blocked', 'list'] as const,
    count: (userId: string) => ['blocked', 'count', userId] as const,
    status: (profileId: string, viewerId: string) =>
      ['blocked', 'status', profileId, viewerId] as const,
  },

  // Users - followers/following lists (viewer-dependent due to is_following/is_mutual fields)
  users: {
    // Username → profileId resolver (separate from profile.byUsername which has different shape)
    byUsername: (usernameLower: string) =>
      ['users', 'byUsername', usernameLower] as const,
    // viewerBucket = auth.user?.id ?? 'anon' (cache varies by viewer)
    followers: (profileId: string, viewerBucket: string) =>
      ['users', profileId, 'followers', viewerBucket] as const,
    following: (profileId: string, viewerBucket: string) =>
      ['users', profileId, 'following', viewerBucket] as const,
    followStatus: (profileId: string, viewerId: string) =>
      ['users', profileId, 'followStatus', viewerId] as const,
    // User search for collaborator picker (ManageCollaboratorsModal)
    // viewerBucket = auth.user?.id ?? 'anon' (cache varies by viewer to prevent cross-user leakage)
    search: (viewerBucket: string, query: string) =>
      ['users', 'search', viewerBucket, query.toLowerCase().trim()] as const,
  },
} as const;

// Type helpers for query keys
export type DiscoverListKey = ReturnType<typeof queryKeys.discover.list>;
export type DiscoverBaseKey = ReturnType<typeof queryKeys.discover.base>;
export type MovieDetailKey = ReturnType<typeof queryKeys.movie.detail>;
export type TVDetailKey = ReturnType<typeof queryKeys.tv.detail>;
export type PersonDetailKey = ReturnType<typeof queryKeys.person.detail>;
export type ProfileByUsernameKey = ReturnType<typeof queryKeys.profile.byUsername>;
export type RatingOneKey = ReturnType<typeof queryKeys.rating.one>;
export type RatingListKey = ReturnType<typeof queryKeys.rating.list>;
export type WatchlistFollowedKey = ReturnType<typeof queryKeys.watchlist.followed>;
export type WatchlistFollowStatusKey = ReturnType<typeof queryKeys.watchlist.followStatus>;
export type ListsMineKey = ReturnType<typeof queryKeys.lists.mine>;
export type ListsDetailKey = ReturnType<typeof queryKeys.lists.detail>;
export type ListsItemsKey = ReturnType<typeof queryKeys.lists.items>;
export type NotificationsUnreadCountKey = ReturnType<typeof queryKeys.notifications.unreadCount>;
export type NotificationsListKey = ReturnType<typeof queryKeys.notifications.list>;
export type NotificationsListBaseKey = ReturnType<typeof queryKeys.notifications.listBase>;
export type SocialFeedKey = ReturnType<typeof queryKeys.social.feed>;
export type SocialFeedBaseKey = ReturnType<typeof queryKeys.social.feedBase>;
export type SearchAllKey = ReturnType<typeof queryKeys.search.all>;
export type ListsDeletedKey = ReturnType<typeof queryKeys.lists.deleted>;
export type ListsDeletedBaseKey = ReturnType<typeof queryKeys.lists.deletedBase>;
export type ListsCollaboratorsKey = ReturnType<typeof queryKeys.lists.collaborators>;
export type UsersSearchKey = ReturnType<typeof queryKeys.users.search>;
export type InboxThreadsKey = ReturnType<typeof queryKeys.inbox.threads>;
export type InboxThreadsBaseKey = ReturnType<typeof queryKeys.inbox.threadsBase>;
export type InboxThreadKey = ReturnType<typeof queryKeys.inbox.thread>;
export type InboxThreadBaseKey = ReturnType<typeof queryKeys.inbox.threadBase>;
export type InboxUnreadCountKey = ReturnType<typeof queryKeys.inbox.unreadCount>;
export type ForYouTopRatingsKey = ReturnType<typeof queryKeys.forYou.topRatings>;
export type ForYouShelvesKey = ReturnType<typeof queryKeys.forYou.shelves>;
export type ForYouTasteProfileKey = ReturnType<typeof queryKeys.forYou.tasteProfile>;
export type ForYouApiKey = ReturnType<typeof queryKeys.forYou.api>;
export type ForYouApiBaseKey = ReturnType<typeof queryKeys.forYou.apiBase>;
export type FeelResultsKey = ReturnType<typeof queryKeys.feel.results>;
export type BlockedCountKey = ReturnType<typeof queryKeys.blocked.count>;

/**
 * Helper to check if discover params are "default" (for bounded persistence).
 * Only default filters + first page are persisted to avoid cache bloat.
 */
export function isDefaultDiscoverParams(params: DiscoverParams): boolean {
  const currentYear = new Date().getFullYear();
  const genres = params.genres ?? [];
  const yearRange = params.yearRange ?? [1920, currentYear];
  const runtimeRange = params.runtimeRange ?? [0, 300];
  const languages = params.languages ?? [];
  const actorSearch = params.actorSearch ?? [];
  const directorSearch = params.directorSearch ?? [];
  const ratingRange = params.ratingRange ?? [0, 10];
  const rtRatingRange = params.rtRatingRange ?? [0, 100];
  const tmdbRatingRange = params.tmdbRatingRange ?? [0, 10];
  const ourRatingRange = params.ourRatingRange ?? [0, 10];

  return (
    genres.length === 0 &&
    yearRange[0] === 1920 &&
    yearRange[1] === currentYear &&
    runtimeRange[0] === 0 &&
    runtimeRange[1] === 300 &&
    languages.length === 0 &&
    actorSearch.length === 0 &&
    directorSearch.length === 0 &&
    params.sortBy === 'popularity.desc' &&
    params.popularOnly === true &&
    ratingRange[0] === 0 &&
    ratingRange[1] === 10 &&
    rtRatingRange[0] === 0 &&
    rtRatingRange[1] === 100 &&
    tmdbRatingRange[0] === 0 &&
    tmdbRatingRange[1] === 10 &&
    ourRatingRange[0] === 0 &&
    ourRatingRange[1] === 10
  );
}
